import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';

interface TimeLog {
  id: string;
  taskId: string;
  userId: string;
  duration: number;
  description?: string;
  loggedAt: string;
  isBillable: boolean;
  workType?: string;
}

interface ActiveTimer {
  id: string;
  taskId: string;
  userId: string;
  startTime: string;
  isActive: boolean;
}

interface TimeTrackingState {
  timeLogs: TimeLog[];
  activeTimer: ActiveTimer | null;
  loading: boolean;
  error: string | null;
}

const initialState: TimeTrackingState = {
  timeLogs: [],
  activeTimer: null,
  loading: false,
  error: null,
};

const timeTrackingSlice = createSlice({
  name: 'timeTracking',
  initialState,
  reducers: {
    setTimeLogs: (state, action: PayloadAction<TimeLog[]>) => {
      state.timeLogs = action.payload;
    },
    addTimeLog: (state, action: PayloadAction<TimeLog>) => {
      state.timeLogs.push(action.payload);
    },
    updateTimeLog: (state, action: PayloadAction<TimeLog>) => {
      const index = state.timeLogs.findIndex(log => log.id === action.payload.id);
      if (index !== -1) {
        state.timeLogs[index] = action.payload;
      }
    },
    deleteTimeLog: (state, action: PayloadAction<string>) => {
      state.timeLogs = state.timeLogs.filter(log => log.id !== action.payload);
    },
    startTimer: (state, action: PayloadAction<ActiveTimer>) => {
      state.activeTimer = action.payload;
    },
    stopTimer: (state) => {
      state.activeTimer = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { 
  setTimeLogs, 
  addTimeLog, 
  updateTimeLog, 
  deleteTimeLog, 
  startTimer, 
  stopTimer, 
  setLoading, 
  setError 
} = timeTrackingSlice.actions;

// Selectors
export const selectTimeLogs = (state: RootState) => state.timeTracking.timeLogs;
export const selectActiveTimer = (state: RootState) => state.timeTracking.activeTimer;
export const selectTimeTrackingLoading = (state: RootState) => state.timeTracking.loading;
export const selectTimeTrackingError = (state: RootState) => state.timeTracking.error;

export default timeTrackingSlice.reducer;